/* istanbul ignore file */
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import useDiscovery from '@modules/discovery/composables/useDiscovery';
import useErrorsHandler from '@composables/useErrorsHandler';
import generateUrlPathUtil from '@utils/generateUrlPathUtil';
import filterUrlQueryParams from '@utils/filterUrlQueryParams';
import xapi from '@services/xapi';

let store;
let collection;
let meta;
let analytics;
let model;
let discovery;
let sortOptions;
let route;
let router;
let pagination;
let selectedFacets;
let currentSortby;
let currentPageindex;
let currentProductsperpage;
let handleError;
let productIds;
const isLoaded = ref(true);
const isLoadFailed = ref(false);
const isLoading = ref(false);
const isSYSLoading = ref(false);

async function loadSortableGrid(pageInformation, options = {}) {
  const isActive = () => (typeof options.isActive === 'function' ? options.isActive() : true);
  isLoaded.value = false;
  isLoadFailed.value = false;
  isLoading.value = true;
  isSYSLoading.value = true;
  try {
    await discovery.loadPage(pageInformation, options);
  } catch (error) {
    if (isActive()) {
      isLoadFailed.value = true;
      handleError.handleError(error);
    }
  }
  if (isActive()) {
    isLoaded.value = true;
    isLoading.value = false;
    isSYSLoading.value = false;
  }
}

function generateUrlPathAndQuery(paginationData, urlRoute) {
  const { query } = urlRoute;
  const isPagination = true;
  const selectedFacetUrl = store.getters.getSelectedFacetUrl;
  const metaBaseUrl = store.getters.getFacetMetaBaseUrl;
  const pathname = generateUrlPathUtil({
    fullPath: urlRoute.fullPath, paginationData, isPagination, selectedFacetUrl, metaBaseUrl,
  });
  return { pathname, query };
}

async function updatePZOBadging() {
  store.commit('SET_PZOURL', await xapi.getPZOBadgingProductList(productIds));
}

function updateSearchUrl(args) {
  store.dispatch('SET_PATHURLS', args);
  const paginationData = computed(() => store.getters.getPathUrls);
  const { pathname, query } = generateUrlPathAndQuery(paginationData.value, route);
  console.info(`Sorting/Pagination URL: ${pathname}`); // eslint-disable-line no-console
  return router.push({ path: pathname, query });
}

function getNextAndPrevUrlToPagination(condition) {
  const currentPageIndex = computed(() => store.getters.getCurrentPageindex);
  /* istanbul ignore next */
  const newPageIndex = Math.max(1, Number(currentPageIndex.value) + (condition === 'positive' ? 1 : -1));

  // Clone the pagination data to avoid mutating the original data directly
  /* istanbul ignore next */
  const updatedPaginationData = [{ key: 'Pageindex', value: newPageIndex }];

  const { pathname, query } = generateUrlPathAndQuery(updatedPaginationData, route);

  // Remove cp_sp
  const urlName = filterUrlQueryParams(pathname);

  return { path: urlName, query };
}

export default function useSortableGrid() {
  store = useStore();
  collection = computed(() => store.getters.getCollection);
  analytics = computed(() => store.getters.getAnalyticsData);
  meta = computed(() => store.getters.getMeta);
  model = computed(() => store.getters.getModel);
  discovery = useDiscovery();
  route = useRoute();
  router = useRouter();
  handleError = useErrorsHandler();
  sortOptions = computed(() => store.getters.getSortOptions);
  selectedFacets = computed(() => {
    // if xapi provides array of selected facets use it instead of selected facets object from response

    // getFacetsSelected -> object is created from the array of selected facets from xapi
    // getSelectedFacets -> object returned from the xapi response, constructed from the URL

    // getFacetsSelected has [] as fallback value if it does not exist
    if (Array.isArray(store?.getters?.getFacetsSelected)) {
      return store.getters.getSelectedFacets;
    }
    return store?.getters?.getFacetsSelected;
  });
  pagination = computed(() => store.getters.getPagination);
  currentSortby = computed(() => store.getters.getCurrentSortby);
  currentPageindex = computed(() => store.getters.getCurrentPageindex);
  currentProductsperpage = computed(() => store.getters.getCurrentProductsperpage);
  productIds = computed(() => store.getters.getProductIds);

  return {
    loadSortableGrid,
    collection,
    meta,
    route,
    model,
    analytics,
    isLoaded,
    isLoadFailed,
    sortOptions,
    updateSearchUrl,
    getNextAndPrevUrlToPagination,
    generateUrlPathAndQuery,
    updatePZOBadging,
    pagination,
    currentSortby,
    currentPageindex,
    currentProductsperpage,
    productIds,
    isLoading,
    selectedFacets,
    isSYSLoading,
  };
}
